"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HMAC = exports.defaults = exports.order = exports.generate = exports.AuthError = void 0;
const crypto_1 = __importDefault(require("crypto"));
const stringToBuffer_1 = __importDefault(require("./stringToBuffer"));
const validateArguments_1 = __importDefault(require("./validateArguments"));
const errors_1 = __importDefault(require("./errors"));
exports.AuthError = errors_1.default;
const generate_1 = __importDefault(require("./generate"));
exports.generate = generate_1.default;
const order_1 = __importDefault(require("./order"));
exports.order = order_1.default;
exports.defaults = {
    algorithm: 'sha256',
    identifier: 'HMAC',
    header: 'authorization',
    maxInterval: 60 * 5,
    minInterval: 0,
};
async function determineSecret(secret, req) {
    return typeof secret === 'function' ? await secret(req) : secret;
}
function HMAC(secret, options = {}) {
    const mergedOpts = { ...exports.defaults, ...options };
    (0, validateArguments_1.default)(secret, mergedOpts);
    return async function (request, _, next) {
        // we have to create a scoped secret per request, if we were to reassign the original `secret` variable
        // the next request that comes in will no longer be the secret function
        // this means we have to explicitly check it's not undefined too
        const scopedSecret = await determineSecret(secret, request);
        if (typeof scopedSecret !== 'string' || scopedSecret.length === 0) {
            return next(new errors_1.default(`Invalid secret. Expected non-empty string but got '${scopedSecret}' (type: ${typeof scopedSecret})`));
        }
        const header = request.get(mergedOpts.header);
        if (typeof header !== 'string') {
            return next(new errors_1.default(`Header provided not in sent headers. Expected ${mergedOpts.header} but not found in request.headers`));
        }
        if (typeof mergedOpts.identifier !== 'string' ||
            !header.startsWith(mergedOpts.identifier)) {
            return next(new errors_1.default(`Header did not start with correct identifier. Expected ${mergedOpts.identifier} but not found in options.header`));
        }
        const unixRegex = /(\d{1,13}):/;
        const unixMatch = unixRegex.exec(header);
        if (!Array.isArray(unixMatch) ||
            unixMatch.length !== 2 ||
            typeof unixMatch[1] !== 'string') {
            return next(new errors_1.default('Unix timestamp was not present in header'));
        }
        // is the unix timestamp difference to current timestamp larger than maxInterval or smaller than minInterval
        const timeDiff = Math.floor(Date.now() / 1000) -
            Math.floor(parseInt(unixMatch[1], 10) / 1000);
        if (timeDiff > mergedOpts.maxInterval ||
            timeDiff * -1 > mergedOpts.minInterval) {
            return next(new errors_1.default('Time difference between generated and requested time is too great'));
        }
        // check HMAC digest exists in header
        const hashRegex = /:(.{1,}$)/;
        const hashMatch = hashRegex.exec(header);
        if (!Array.isArray(hashMatch) ||
            hashMatch.length !== 2 ||
            typeof hashMatch[1] !== 'string') {
            return next(new errors_1.default('HMAC digest was not present in header'));
        }
        const hmac = (0, generate_1.default)(scopedSecret, mergedOpts.algorithm, unixMatch[1], request.method, request.originalUrl, request.body, { order: mergedOpts.order }).digest();
        const sourceDigest = (0, stringToBuffer_1.default)(hashMatch[1]); // convert string to buffer
        // use timing safe check to prevent timing attacks
        if (hmac.length !== sourceDigest.length ||
            !crypto_1.default.timingSafeEqual(hmac, sourceDigest)) {
            return next(new errors_1.default("HMAC's did not match"));
        }
        return next();
    };
}
exports.HMAC = HMAC;
